import React, { useContext } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Link,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Button } from "../components";
import { AuthContext } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import api from "../api/axiosInstance";
import { ADMIN_PERMISSIONS, SUPER_ADMIN_PERMISSIONS, DONOR_PERMISSIONS } from "../data/roles";
import { UserRolesEnum } from "../typings";
import Logo from "../assets/holding-hands.svg";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const Login: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", values.email);
      formData.append("password", values.password);
      formData.append("grant_type", "password");
      formData.append("client_id", "QeKTkPwJmsXjBvIr26SsFUM4R32T4ScH6nRb6Y2H");
      formData.append(
        "client_secret",
        "f2XHUC8HmpftuQfGBLM3T0uaKfwSDxdWv8xXvXQMPWIrrjR4KFEfJzZgqy8oNHanSPBXCkIFYc6YZOx3WP7eqHfyoOPD9r3Kv6LmPsXTCxDwzlgBENVNTaYVTr6Ogpzp"
      );

      const response = await api.post("/auth/login/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const token = response.data.data.access_token;
      const userRole = response.data.data.user.role;
      const userId = response.data.data.user.id;
      auth?.login(token, userRole, userId);

      showSnackbar("Login successful! Welcome back.", "success");

      if (
        [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS, ...DONOR_PERMISSIONS].includes(
          userRole as UserRolesEnum
        )
      ) {
        navigate("/home");
      } else {
        navigate("/applications");
      }
    } catch (e: any) {
      const errorMessage = e?.response?.data?.detail || 
                          e?.response?.data?.message || 
                          "Login failed. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(140deg, #eaf3ff 0%, #f5f8ff 100%)",
        p: 2,
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: { xs: "100%", sm: 420 },
          // p: { xs: '3', sm: 5 },
          padding:'10px 30px 20px 30px',
          borderRadius: 5,
          backgroundColor: "#ffffff",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Box
            component="img"
            // @ts-ignore
            src={Logo}
            alt="Organization logo"
            sx={{ height: 160 }}
          />

          <Typography variant="h4" fontWeight="600" sx={{ color: "#212B36" }}>
            Sign in to your account
          </Typography>
          <Typography sx={{ color: "#637381", fontSize: 16, mb: 1 }}>
            Access your holding hands dashboard
          </Typography>
        </Stack>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form noValidate>
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                name="email"
                type="email"
                label="Email address"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                name="password"
                type="password"
                id="password"
                label="Password"
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <Button type="submit" fullWidth sx={{ mt: 3 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
            </Form>
          )}
        </Formik>

        <Typography
          variant="body2"
          align="center"
          mt={2}
          sx={{ color: "#4B5563" }}
        >
          New to the platform?{" "}
          <Link
            component={RouterLink}
            to="/signup"
            underline="hover"
            sx={{ color: "#1A73E8", fontWeight: 600 }}
          >
            Create an account
          </Link>
        </Typography>

        <Box textAlign="center" mt={1}>
          <Typography variant="body2" align="center">
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              sx={{ color: "#1A73E8", fontWeight: 600 }}
            >
              Forgot password?
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
